import loaderSignal from 'signals/Loader.signal';
import searchBusinessResultsSignal from './searchBusinessResults.signal';
import saveBusinessSearchSignal from '../SearchBusinessSaveModal/saveBusinessSearchModal.signal';
import alertSignal from 'signals/Alert.signal';
import api from 'api/api';
import history from '../../../history';
import userAccountSignal from '../../../signals/UserAccount.signal';
import { saveSearch } from '../SearchBusinessSaveModal/saveBusinessSearchModal.helpers';
import $appSettings from 'signals/AppSettings.signal';

const xFormAppliedFiltersToWhereClause = () => {
  const portalType = userAccountSignal.value.userData.account.portalType;
  const { PORTAL_TYPES } = $appSettings.value.constants;

  const params =
    Object.fromEntries(new URLSearchParams(history.location.search)) || {};

  const conditions = [];

  if (params.id) {
    if (params.id.includes(',')) {
      conditions.push({ id: { in: params.id.split(',').map((id) => Number(id.trim())) } });
    } else {
      conditions.push({ id: { in: [Number(params.id)] } });
    }
  }

  if (params.name) {
    const nameTrimmed = params.name.trim();
    if (nameTrimmed) {
      conditions.push({ name: { contains: nameTrimmed, mode: 'insensitive' } });
    }
  }

  if (params.industry) {
    const grouped = $appSettings.value.constants?.INDUSTRY_OPTIONS_GROUPED ?? [];
    const sectorGroup = grouped.find((g) => g.label === params.industry);
    const industriesToMatch = sectorGroup
      ? sectorGroup.options.map((o) => o.value)
      : [params.industry];

    if (industriesToMatch.length === 1) {
      conditions.push({
        assistance: {
          path: ['industrySelection'],
          array_contains: industriesToMatch[0],
        },
      });
    } else if (industriesToMatch.length > 1) {
      conditions.push({
        OR: industriesToMatch.map((ind) => ({
          assistance: {
            path: ['industrySelection'],
            array_contains: ind,
          },
        })),
      });
    }
  }

  if (params.location) {
    conditions.push({
      address: {
        path: ['postalCode'],
        equals: params.location,
      },
    });
  }

  if (params.favorite) {
    if (params.favorite === 'yes') {
      if (portalType === PORTAL_TYPES.lender) {
        conditions.push({
          favoritedByLenders: {
            some: {
              lenderId: userAccountSignal.value.userData.account.lender?.id,
            },
          },
        });
      } else if (portalType === PORTAL_TYPES.vc) {
        conditions.push({
          favoritedByVcs: {
            some: {
              ventureCapitalFirmId:
                userAccountSignal.value.userData.account.ventureCapitalFirm?.id,
            },
          },
        });
      }
    } else {
      conditions.push({
        favoritedByLenders: { none: {} },
        favoritedByVcs: { none: {} },
      });
    }
  }

  if (params.fundingRange) {
    const fundingRangeSplit = params.fundingRange
      .split('-')
      .map((r) => r.replace(/[^\d.]/g, ''));

    let min, max;

    if (fundingRangeSplit.length === 1) {
      min = Number(fundingRangeSplit[0]);
      max = 10000000;
    } else {
      min = Number(fundingRangeSplit[0]);
      max = Number(fundingRangeSplit[1]);
    }

    if (Number.isFinite(min) && Number.isFinite(max)) {
      conditions.push({
        AND: [
          { assistance: { path: ['seekingMin'], lte: max } },
          { assistance: { path: ['seekingMax'], gte: min } },
        ],
      });
    }
  }

  if (conditions.length === 0) return {};
  if (conditions.length === 1) return conditions[0];
  return { AND: conditions };
};

const xFormSortToOrderByClause = () => {
  const { sort } =
    Object.fromEntries(new URLSearchParams(history.location.search)) || {};

  if (!sort) {
    return undefined;
  }

  return [{ [sort.split('-')[0]]: sort.split('-')[1] }];
};

export const fetchAndSetSearchResults = async () => {
  const params = Object.fromEntries(new URLSearchParams(history.location.search)) || {};
  const page = params.page ? Number(params.page) : 1;
  const { recordsPerPage } = searchBusinessResultsSignal.value;

  loaderSignal.update({
    isContentLoading: true,
  });
  try {
    const options = {
      where: xFormAppliedFiltersToWhereClause(),
      orderBy: xFormSortToOrderByClause(),
      include: {
        favoritedByLenders: true,
        favoritedByVcs: true,
      },
      getCount: true,
      take: recordsPerPage,
      skip: (page - 1) * recordsPerPage,
    };
    const data = await api.post({
      path: '/v2/businesses',
      body: { options },
    });
    const businesses = data.businesses ?? data;
    const count = data.count;
    const selectedBusiness = searchBusinessResultsSignal.value.selectedBusiness;

    return searchBusinessResultsSignal.update({
      businesses,
      selectedBusiness: selectedBusiness
        ? businesses.find((b) => b.id === selectedBusiness.id)
        : null,
      totalPages: count != null ? Math.ceil(count / recordsPerPage) : 0,
      totalCount: count ?? 0,
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      message: error.message,
      error,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const setDropdownFilter = ({ name, value }) =>
  searchBusinessResultsSignal.update({
    dropdownFilters: {
      ...searchBusinessResultsSignal.value.dropdownFilters,
      [name]: value,
    },
  });

export function hideModal() {
  return searchBusinessResultsSignal.update({
    selectedBusiness: null,
    modalOpen: false,
  });
}

export function toggleModal(selectedBusiness) {
  return searchBusinessResultsSignal.update({
    selectedBusiness,
    modalOpen: true,
  });
}

export const markBusinessAsFavorite = async (business, callback = null) => {
  loaderSignal.update({
    isContentLoading: true,
    message: 'Saving business as favorite...',
  });
  const portalType = userAccountSignal.value.userData.account.portalType;
  const { PORTAL_TYPES } = $appSettings.value.constants;

  const data = {
    businessId: business.id,
  };

  if (portalType === PORTAL_TYPES.lender) {
    data.lenderId = userAccountSignal.value.userData.account.lender?.id;
  } else if (portalType === PORTAL_TYPES.vc) {
    data.ventureCapitalFirmId =
      userAccountSignal.value.userData.account.ventureCapitalFirm?.id;
  }

  try {
    await api.post({
      path:
        portalType === PORTAL_TYPES.lender
          ? '/lenderFavorites'
          : '/ventureCapitalFirmFavorites',
      body: {
        data,
      },
    });

    if (callback) {
      await callback();
    } else {
      await fetchAndSetSearchResults();
    }
    return alertSignal.update({
      type: 'notification',
      variant: 'success',
      message: 'Successfully favorited business.',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      message: error.message,
      error,
    });
  } finally {
    loaderSignal.reset();
  }
};

export const deleteBusinessFavorite = async (
  businessFavorite,
  callback = null
) => {
  const { PORTAL_TYPES } = $appSettings.value.constants;
  loaderSignal.update({
    isContentLoading: true,
    message: 'Removing business from favorite...',
  });

  const portalType = userAccountSignal.value.userData.account.portalType;
  try {
    await api.delete({
      path:
        portalType === PORTAL_TYPES.lender
          ? '/lenderFavorites'
          : '/ventureCapitalFirmFavorites',
      body: {
        where: {
          id: businessFavorite.id,
        },
      },
    });

    if (callback) {
      await callback();
    } else {
      await fetchAndSetSearchResults();
    }
    return alertSignal.update({
      type: 'notification',
      variant: 'success',
      message: 'Successfully unfavorited business.',
    });
  } catch (error) {
    return alertSignal.update({
      type: 'notification',
      message: error.message,
      error,
    });
  } finally {
    loaderSignal.reset();
  }
};

const syncSaveBusinessSearchFromFilters = (filters = {}) => {
  saveBusinessSearchSignal.update({
    industry: filters.industry || '',
    location: filters.location || '',
    fundingRange: filters.fundingRange || '',
  });
};

export const resetBusinessSearchFilters = () => {
  searchBusinessResultsSignal.update({ dropdownFilters: {} });
  syncSaveBusinessSearchFromFilters({});
};

export const saveSearchButtonIsDisabled = () => {
  const params = Object.fromEntries(
    new URLSearchParams(history.location.search)
  );

  if (!params.industry && !params.location && !params.fundingRange) {
    return true;
  }

  return false;
};

export const getRelevantSearchParams = () => {
  const params = Object.fromEntries(
    new URLSearchParams(history.location.search)
  );

  const relevantSearchParamLabels = ['industry', 'location', 'fundingRange'];

  const currentSearchTerms = [];

  relevantSearchParamLabels.forEach((label) => {
    if (!!params[label]) {
      currentSearchTerms.push({ label, value: params[label] });
    }
  });

  return currentSearchTerms;
};

export const handleSaveSearchClick = async () => {
  await saveSearch();
};
